/** TO DO: fix the rule */
/* eslint-disable @ngrx/on-function-explicit-return-type */
import { createReducer, on } from '@ngrx/store';
import {
  clearOrgEntitlementsRefresh,
  clearRefreshTenantListGrid,
  clearUserEntitlementsGridRefresh,
  clearUserRolesGridRefresh,
  createTenantSuccess,
  customProvisioningCompleted,
  getUserPreferencesSuccess,
  leaveCustomProvisioning,
  noLastAccessedTenant,
  refreshOrgEntitlements,
  refreshTenantListGrid,
  refreshUserEntitlementsGrid,
  refreshUserRolesGrid,
  resetAllocateEntitlementResponseData,
  setAllocateEntitlementResponseData,
  setAllTenantResourceRolesSuccess,
  setCanChangeTenantHierarchy,
  setCurrentLanguage,
  setEditUserDetails,
  setFavoriteAppsSuccess,
  setLoggedInUserRoleHierarchy,
  setNoSuggestedTenantByCodeFound,
  setPersonalTenantContext,
  setPreferencesSuccess,
  setSuggestedTenantByCodeSuccess,
  setTenantCreated,
  setTenantDetails,
  setTenantEffectiveRoles,
  setTenantId,
  setTenantLogo,
  setTenantResourceHierarchy,
  setTenantServices,
  setTenantUsers,
  setTenantUtilityTokens,
  setUnallocatedEntitlementsCount,
  setUserId,
  setUserMembership,
  setUserPermissionsEvalResult,
  setUserResourcesSuccess,
  setUserRolesForTenantSuccess,
  updateAccessibleTenants,
  updateTenantNameSuccess,
  updateUserDescriptionOnSuccess,
  userData,
  userLosesAccessOnParentTenant,
  waitForCustomProvisioningComplete,
} from './user-data.action';

import {
  DefaultLanguage,
  Delayed,
  ITenantDetail,
  Resource,
  Role,
  ROLES_WITH_MANAGE_ACCESS,
  SelectedTenant,
  TenantAccessType,
  TenantContext,
  TenantResourceHierarchy,
  UserPreferences,
  UserRole,
} from '@ra-state';
import { PermsEvaluationResult } from '@servicesV2/permissions.service';
import {
  appendTenantLogoEtag,
  preserveTenantLogoCacheBust,
  updateServicesAllocationUserInformation,
} from '../../v2/shared/utils';
import { UserRoleHierarchy } from '../user-access';

export const userDataInitialState: UserPreferences = {
  businessPartnerId: '',
  company: '',
  email: '',
  eulaAcceptanceRequired: false,
  eulaContentUrl: '',
  eulaVersion: '',
  firstName: '',
  lastName: '',
  location: '',
  name: '',
  notificationCounts: { totalNotifications: 0, unreadNotifications: 0 },
  notifications: [],
  newTenantCreated: false,
  services: [],
  preferences: {
    disableTokenNotification: false,
    language: DefaultLanguage,
  },
  hasOrgEmailProvider: false,
  refreshUserEntitlementsGrid: false,
  refreshOrgEntitlementsGrid: false,
  refreshUserRolesGrid: false,
  unallocatedEntitlementsCount: {
    entitlementCounts: [],
    totalCreditsEntitlementCount: 0,
    totalEntitlementCount: 0,
  },
  manageableResources: new Delayed<Resource[]>(),
  userPermissionsEvalResult: new Delayed<PermsEvaluationResult>(),
  currentTenantContext: new Delayed<TenantContext>(),
  canChangeTenantHierarchy: false,
  userMembership: {
    hasAnyOwnerRole: false,
    hasDirectRole: false,
  },
  hasAnyServiceRole: new Delayed<TenantAccessType>(),
  userRoleHierarchy: new Delayed<UserRoleHierarchy>(),
  tenantResourceHierarchy: new Delayed<TenantResourceHierarchy>(),
};

const getCachedTenantLogoUrl = (state: UserPreferences, tenantId: string): string | undefined => {
  if (state.currentTenant?.id === tenantId && state.currentTenant.logoUrl) {
    return state.currentTenant.logoUrl;
  }

  return state.accessibleTenants?.find(({ id }) => id === tenantId)?.logoUrl;
};

const mergeAccessibleTenantLogoUrls = (
  state: UserPreferences,
  accessibleTenants: UserPreferences['accessibleTenants'],
): UserPreferences['accessibleTenants'] =>
  accessibleTenants?.map((tenant) => {
    const cacheBustSourceUrl = getCachedTenantLogoUrl(state, tenant.id);
    return tenant.logoUrl && cacheBustSourceUrl
      ? { ...tenant, logoUrl: preserveTenantLogoCacheBust(tenant.logoUrl, cacheBustSourceUrl) }
      : tenant;
  });

export const setUserData = createReducer(
  userDataInitialState,
  on(userData, (state: UserPreferences, { value }) => ({
    ...state,
    businessPartnerId: value.businessPartnerId,
    company: value.company,
    email: value.email,
    eulaAcceptanceRequired: value.eulaAcceptanceRequired,
    eulaContentUrl: value.eulaContentUrl,
    eulaVersion: value.eulaVersion,
    firstName: value.firstName,
    lastName: value.lastName,
    location: value.location,
    name: value.name,
    hasOrgEmailProvider: value.hasOrgEmailProvider,
  })),
  on(setUserId, (state: UserPreferences, { payload }) => ({
    ...state,
    userId: payload,
  })),
  on(setCurrentLanguage, (state: UserPreferences, { preferredLanguage }) => ({
    ...state,
    preferences: { ...state.preferences, language: preferredLanguage },
  })),
  on(setTenantId, (state: UserPreferences, { value }) => ({
    ...state,
    currentTenantId: value,
    hasAnyServiceRole: new Delayed<TenantAccessType>(),
  })),
  on(createTenantSuccess, (state: UserPreferences) => ({
    ...state,
    newTenantCreated: true,
  })),
  on(setTenantCreated, (state: UserPreferences, { value }) => ({
    ...state,
    newTenantCreated: value,
  })),
  on(setTenantDetails, (state: UserPreferences, { payload }) => ({
    ...state,
    currentTenant:
      payload && state.currentTenant?.id === payload.id
        ? {
            ...payload,
            logoUrl: preserveTenantLogoCacheBust(payload.logoUrl, state.currentTenant.logoUrl),
          }
        : payload,
  })),
  on(setTenantLogo, (state: UserPreferences, { tenantId, etag }) => {
    if (!etag) {
      return state;
    }

    return {
      ...state,
      currentTenant:
        state.currentTenant?.id === tenantId && state.currentTenant.logoUrl
          ? { ...state.currentTenant, logoUrl: appendTenantLogoEtag(state.currentTenant.logoUrl, etag) }
          : state.currentTenant,
      accessibleTenants: state.accessibleTenants?.map((tenant) =>
        tenant.id === tenantId && tenant.logoUrl
          ? { ...tenant, logoUrl: appendTenantLogoEtag(tenant.logoUrl, etag) }
          : tenant,
      ),
    };
  }),
  on(userLosesAccessOnParentTenant, (state: UserPreferences) => ({
    ...state,
    currentTenant: undefined,
    currentTenantContext: new Delayed<TenantContext>('NotSelected'),
    currentTenantId: undefined,
  })),
  on(getUserPreferencesSuccess, (state: UserPreferences, { value }) => ({
    ...state,
    accessibleTenants: mergeAccessibleTenantLogoUrls(state, value.accessibleTenants),
    businessPartnerId: value.businessPartnerId,
    company: value.company,
    email: value.email,
    eulaAcceptanceRequired: value.eulaAcceptanceRequired,
    eulaContentUrl: value.eulaContentUrl,
    eulaVersion: value.eulaVersion,
    firstName: value.firstName,
    lastName: value.lastName,
    location: value.location,
    name: value.name,
    preferences: {
      ...state.preferences,
      lastAccessedTenantId: value.preferences?.lastAccessedTenantId,
      disableTokenNotification:
        value.preferences?.disableTokenNotification || state.preferences?.disableTokenNotification,
      favoriteApps: value.preferences?.favoriteApps,
      language: value.preferences?.language,
    },
    hasOrgEmailProvider: value.hasOrgEmailProvider,
  })),
  on(setPreferencesSuccess, (state: UserPreferences, { tenantId }) => ({
    ...state,
    preferences: {
      ...state.preferences,
      lastAccessedTenantId: tenantId,
    },
  })),
  on(setTenantEffectiveRoles, (state: UserPreferences, { effectiveRoles }) => {
    const tenantRole = effectiveRoles.find((role) => role.resourceType === 'Tenant');
    const manageableResources = effectiveRoles
      .filter((role) => ROLES_WITH_MANAGE_ACCESS.includes(role.role))
      .map((resource) => {
        return {
          resourceId: resource.resourceId,
          resourceName: resource.resourceName,
          resourceType: resource.resourceType,
        };
      });
    const hasAnyServiceRole = effectiveRoles.some((role) => role.resourceType === 'Service' && role.role);
    let tenantAccessType;
    if (hasAnyServiceRole) {
      tenantAccessType = 'Service';
    } else if (effectiveRoles.some((role) => role.resourceType === 'Tenant' && role.role === Role.Navigator)) {
      tenantAccessType = 'SubOrgs';
    }
    return {
      ...state,
      effectiveRoles: effectiveRoles,
      hasAnyServiceRole: new Delayed<TenantAccessType>(tenantAccessType),
      manageableResources: new Delayed<Resource[]>(manageableResources),
      currentTenantContext: new Delayed<TenantContext>({
        id: tenantRole?.tenantId,
        name: tenantRole?.resourceName,
        role: tenantRole?.role,
      } as TenantContext),
    };
  }),
  on(setAllTenantResourceRolesSuccess, (state: UserPreferences, { payload }) => ({
    ...state,
    resourceRoles: payload,
  })),
  on(setUserRolesForTenantSuccess, (state: UserPreferences, { payload }) => ({
    ...state,
    userResourceRoles: payload,
  })),
  on(setUserResourcesSuccess, (state: UserPreferences, { payload }) => ({
    ...state,
    resources: payload,
  })),
  on(setTenantUsers, (state: UserPreferences, { selectedTenantUsers }): UserPreferences => ({
    ...state,
    selectedTenantUsers,
  })),
  on(setSuggestedTenantByCodeSuccess, (state: UserPreferences, { payload }) => ({
    ...state,
    suggestedTenant: payload,
  })),
  on(setNoSuggestedTenantByCodeFound, (state: UserPreferences, { payload }) => ({
    ...state,
    noSuggestedTenant: payload,
  })),
  on(setEditUserDetails, (state: UserPreferences, { payload }) => ({
    ...state,
    editUser: payload as UserRole,
  })),
  on(updateTenantNameSuccess, (state: UserPreferences, { value }) => {
    return {
      ...state,
      currentTenantContext: new Delayed<TenantContext>({
        id: (state.currentTenantContext.value as SelectedTenant)?.id,
        role: (state.currentTenantContext.value as SelectedTenant)?.role,
        name: value,
      }),
    };
  }),

  // on(joinTenantSuccess, (state: UserPreferences) => ({
  //   ...state,
  //   showJoinSuccessInlineMessage: true,
  // })),

  on(setTenantServices, (state: UserPreferences, { payload }) => ({
    ...state,
    services: payload,
  })),
  on(setTenantUtilityTokens, (state: UserPreferences, { payload }) => ({
    ...state,
    tenantUtilityTokens: payload,
  })),
  on(updateUserDescriptionOnSuccess, (state: UserPreferences, { entitlementId, allocationId, userDescription }) => ({
    ...state,
    currentTenant: state.currentTenant
      ? {
          ...state.currentTenant,
          services: updateServicesAllocationUserInformation(
            state.currentTenant.services,
            entitlementId,
            allocationId,
            userDescription,
          ),
          scheduledForActivationServices: updateServicesAllocationUserInformation(
            state.currentTenant.scheduledForActivationServices,
            entitlementId,
            allocationId,
            userDescription,
          ),
        }
      : state.currentTenant,
  })),
  on(waitForCustomProvisioningComplete, (state: UserPreferences, { payload }) => ({
    ...state,
    currentCustomProvisioningData: payload,
    exitFromProvisioning: false,
  })),
  on(customProvisioningCompleted, leaveCustomProvisioning, (state: UserPreferences) => ({
    ...state,
    currentCustomProvisioningData: undefined,
    exitFromProvisioning: true,
  })),
  on(setFavoriteAppsSuccess, (state: UserPreferences, { favoriteApps }) => ({
    ...state,
    preferences: {
      ...state.preferences,
      favoriteApps: favoriteApps,
    },
  })),
  on(noLastAccessedTenant, (state: UserPreferences) => ({
    ...state,
    currentTenantContext: new Delayed<TenantContext>('NotSelected'),
    manageableResources: new Delayed<Resource[]>([]),
    hasAnyServiceRole: new Delayed<TenantAccessType>(undefined),
    effectiveRoles: [],
    currentTenant: {} as ITenantDetail,
    currentTenantId: undefined,
  })),
  on(refreshOrgEntitlements, (state: UserPreferences) => ({ ...state, refreshOrgEntitlementsGrid: true })),
  on(clearOrgEntitlementsRefresh, (state: UserPreferences) => ({ ...state, refreshOrgEntitlementsGrid: false })),
  on(refreshUserEntitlementsGrid, (state: UserPreferences) => ({ ...state, refreshUserEntitlementsGrid: true })),
  on(clearUserEntitlementsGridRefresh, (state: UserPreferences) => ({ ...state, refreshUserEntitlementsGrid: false })),
  on(refreshUserRolesGrid, (state: UserPreferences) => ({ ...state, refreshUserRolesGrid: true })),
  on(clearUserRolesGridRefresh, (state: UserPreferences) => ({ ...state, refreshUserRolesGrid: false })),
  on(setUnallocatedEntitlementsCount, (state: UserPreferences, { payload }) => ({
    ...state,
    unallocatedEntitlementsCount: payload,
  })),
  on(setAllocateEntitlementResponseData, (state: UserPreferences, { payload }): UserPreferences => {
    const newState = { ...state, allocateEntitlementResponse: payload.errorData };
    return newState;
  }),
  on(resetAllocateEntitlementResponseData, (state): UserPreferences => {
    const newState = { ...state, allocateEntitlementResponse: undefined };
    return newState;
  }),
  on(refreshTenantListGrid, (state: UserPreferences) => ({ ...state, refreshTenantListGrid: true })),
  on(clearRefreshTenantListGrid, (state: UserPreferences) => ({ ...state, refreshTenantListGrid: false })),
  on(setPersonalTenantContext, (state: UserPreferences) => ({
    ...state,
    currentTenantContext: new Delayed<TenantContext>('Personal'),
    manageableResources: new Delayed<Resource[]>([]),
    hasAnyServiceRole: new Delayed<TenantAccessType>(undefined),
  })),
  on(updateAccessibleTenants, (state: UserPreferences, { accessibleTenants }) => {
    return {
      ...state,
      accessibleTenants: mergeAccessibleTenantLogoUrls(state, accessibleTenants),
    };
  }),
  on(setCanChangeTenantHierarchy, (state: UserPreferences, { value }) => ({
    ...state,
    canChangeTenantHierarchy: value,
  })),
  on(setUserMembership, (state: UserPreferences, { payload }) => ({
    ...state,
    userMembership: payload,
  })),
  on(setUserPermissionsEvalResult, (state: UserPreferences, { result }) => ({
    ...state,
    userPermissionsEvalResult: new Delayed<PermsEvaluationResult>(result),
  })),
  on(setLoggedInUserRoleHierarchy, (state: UserPreferences, { userRoleHierarchy }): UserPreferences => ({
    ...state,
    userRoleHierarchy: new Delayed<UserRoleHierarchy>(userRoleHierarchy),
  })),
  on(setTenantResourceHierarchy, (state: UserPreferences, { resourceHierarchy }): UserPreferences => ({
    ...state,
    tenantResourceHierarchy: new Delayed<TenantResourceHierarchy>(resourceHierarchy),
  })),
);
